import { parseLatLongString } from '../../core/util/geo';
import { dateFormat, parseSqlDateTime } from '../../core/util/date';
import { isAlwaysOpen } from '../../core/util/location';
import { isEmptyString } from '../../core/util/string';
import { isEmptyObject } from '../../core/util/object';
import {
  TIME_FORMAT,
  TIME_LAST_MINUTE_OF_THE_DAY,
  TIME_LAST_SECOND_OF_THE_DAY,
} from '../../constants';
import { buildStreetViewUrl } from '../../core/google/staticmap';
import moment from 'moment';
import { isMidnightish } from '../../core/util/time';

const openingHoursFormatter = interval => {
  if (isEmptyObject(interval)) {
    return '';
  }

  const now = dateFormat(Date.now(), 'isoDate');
  const startTime = dateFormat(parseSqlDateTime(`${now} ${interval.fromTime}`), TIME_FORMAT);
  const endTime = isMidnightish(interval.toTime)
    ? 'midnight'
    : dateFormat(parseSqlDateTime(`${now} ${interval.toTime}`), TIME_FORMAT);

  return `${startTime} to ${endTime}`;
};

const services = [
  {
    key: 'isPediatricPatientsAccepted',
    label: 'Pediatrics',
  },
  {
    key: 'isWellWomanExamAvailable',
    label: "Women's Health",
  },
  {
    key: 'isXRayAvailable',
    label: 'X-rays',
  },
];

/**
 * Normalize location data into a friendly format.
 * @param {object} location
 * @return {object}
 */
const locationResponseFormatter = location => {
  if (isEmptyObject(location)) return {};

  if (location.isFormatted) {
    return location;
  }

  const formatted = {};
  const regex = /_(\w)/g;
  for (const key in location) {
    if (!location.hasOwnProperty(key)) {
      continue;
    }

    const camelCaseKey = key.replace(regex, (m, w) => w.toUpperCase());
    let value = null;
    switch (camelCaseKey) {
      case 'latLong':
        value = parseLatLongString(location.lat_long);
        break;
      case 'hoursDefault':
      case 'hours':
        value = {};
        for (const day in location[key]) {
          if (location[key].hasOwnProperty(day)) {
            value[day] = location[key][day].map(hourInterval => ({
              fromTime: hourInterval.from_time,
              toTime: hourInterval.to_time,
              isSpecial: Boolean(hourInterval.is_special),
            }));
          }
        }

        break;
      case 'slots':
        value = location.slots.map(slot => ({
          availability: slot.availability,
          appointmentDate: moment(slot.appointment_date).valueOf(),
          isBusy: slot.busy,
          isReservationsDisabled: slot.is_reservations_disabled,
        }));
        break;
      default:
        value = location[key];
    }

    formatted.facilities = [];
    for (const service of services) {
      if (location[service.key]) {
        formatted.facilities.push(service);
      }
    }

    formatted[camelCaseKey] = value;
  }

  if (!isEmptyString(location.display_name_primary)) {
    formatted.name = location.display_name_primary;
  }

  if (!location.image_links) {
    formatted.imageLinks = {};
  }

  if (
    !location.image_links ||
    !location.image_links.hasOwnProperty('image') ||
    location.image_links.image.length === 0
  ) {
    const addressAttributes = {
      address: formatted.address,
      city: formatted.city,
      state: formatted.state,
      latLong: formatted.latLong,
    };
    formatted.imageLinks.image = [buildStreetViewUrl(addressAttributes, 160, 100)];
  }

  formatted.defaultImage = formatted.imageLinks.image[0];

  formatted.isFormatted = true;
  formatted.isOpaque = false;
  if (!location.hasOwnProperty('isBestMatch')) {
    formatted.isBestMatch = false;
  }

  return formatted;
};

const locationSlotsFormatter = (response = { slots: [] }) => {
  const { slots } = response;
  if (slots.isFormatted) return slots;

  let formatted = {};
  formatted.slots = slots.map(slot => ({
    availability: slot.availability,
    appointmentDate: moment(slot.appointment_date).valueOf(),
    isBusy: slot.busy,
    isReservationsDisabled: slot.is_reservations_disabled,
  }));

  formatted.isFormatted = true;
  formatted.timeZone = response.time_zone;
  return formatted;
};

const publicBookingLocationFormatter = response => ({
  name: response.location_display_name_primary,
  id: response.location_id,
});

export {
  publicBookingLocationFormatter,
  locationResponseFormatter,
  openingHoursFormatter,
  locationSlotsFormatter,
};
